import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { translate } from '../../../services/translate';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import useFilters from '../../../components/Filter/hooks/useFilters';
import MyDataTable from '../../../components/MyDataTable';
import { Pagination } from '../../../components/Pagination';
import moment from 'moment';
import DropdownButton from '../../../components/DropdownButton';
import { PlateData } from '../../../services/central-api/license-plate-recognition';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import { dashboardSummaryService } from '../../../services/central-api/dashboard-summary';
import RegisteredPlateModal from './RegisteredPlateModal';
import Badge, { BadgeColor } from '../../../components/Badge';
import MyAppText from '../../../components/MyAppText';
import FilterInput from '../../../components/Filter/components/FilterInput';
import DeleteRegisteredPlateModal from './DeleteRegisteredPlateModal';
import RegisteredPlateFilter from './RegisteredPlateFilter';

interface Props {
    showCreateModal: boolean;
    setShowCreateModal: Dispatch<SetStateAction<boolean>>;
}

export interface PlateTableFilters {
    page: number;
    limit: number;
    textFilter: string | undefined;
    isActive?: boolean;
    groupsIds?: number[];
}

export default function RegisteredPlateTable({ setShowCreateModal, showCreateModal }: Props) {
    const { styles } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const [totalItems, setTotalItems] = useState(0);
    const [plates, setPlates] = useState<PlateData[]>([]);

    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteIsModalVisible] = useState<boolean>(false);
    const [selectedPlate, setSelectedPlate] = useState<PlateData | undefined>();

    const [filters, setFilters] = useFilters<PlateTableFilters>({
        textFilter: undefined,
        page: 0,
        limit: 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    const queryResult = useQuery({
        queryKey: ['plateGroupModal', filters],
        queryFn: () => dashboardSummaryService.getMonitoringPlates(filters),
        enabled: !!authenticatedUser,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setPlates(data.rows);
        setTotalItems(data.count);
    }, [data]);

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            setPlates([]);
            setTotalItems(0);
            refetch();
        }
    }, [isFocused, filters]));

    function getPlateStatus(isActive: boolean) {
        let customName = 'active';
        let badgeColor: BadgeColor = 'green';

        if (!isActive) {
            customName = 'inactive';
            badgeColor = 'red';
        }

        return <View style={[{ justifyContent: 'center' }]}>
            <Badge mode='SQUARE' color={badgeColor}>{translate(customName)}</Badge>
        </View>;
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return (
        <>
            <View style={styles.cardListWrapper}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', minHeight: 40, zIndex: 2, gap: 8 }}>
                    <MyAppText style={styles.mainHeaderText}>{translate('registeredPlates')}</MyAppText>
                    <View style={styles.flex} />
                    <View style={styles.inputWrapper}>
                        <FilterInput
                            value={textFilter}
                            placeholder={translate('typeToSearch')}
                            onChange={setTextFilter}
                            onKeyPress={handleKeyDown}
                        />
                    </View>
                    <View style={[styles.buttonsContainer, { zIndex: 2 }]}>
                        <RegisteredPlateFilter
                            filters={filters}
                            setFilters={setFilters}
                            textFilter={textFilter}
                            setTextFilter={setTextFilter}
                        />
                    </View>
                </View>
                <View style={styles.flex}>
                    <View style={styles.cardList}>
                        <MyDataTable
                            columns={[
                                {
                                    name: translate('status'),
                                    cell: row => getPlateStatus(row.isActive),
                                    grow: 0,
                                },
                                {
                                    name: translate('plate'),
                                    cell: row => row.plate,
                                    grow: 1,
                                },
                                {
                                    name: translate('groups'),
                                    cell: row => row.groups?.map(g => g.name).join(', '),
                                    grow: 1,
                                },
                                {
                                    name: translate('description'),
                                    cell: row => row.description,
                                    grow: 1,
                                },
                                {
                                    name: translate('registeredOn'),
                                    cell: row => row.createdAt ? moment(row.createdAt).format('DD/MM/YYYY') : '',
                                    grow: 1,
                                },
                                {
                                    name: '',
                                    wrap: true,
                                    button: true,
                                    cell: (row) => {
                                        return <DropdownButton
                                            actions={[
                                                {
                                                    content: translate('editInformation'),
                                                    onPress: () => {
                                                        setSelectedPlate(row);
                                                        setIsModalVisible(true);
                                                    }
                                                },
                                                {
                                                    content: translate('delete'),
                                                    variant: 'danger',
                                                    onPress: () => {
                                                        setSelectedPlate(row);
                                                        setIsDeleteIsModalVisible(true);
                                                    }
                                                }
                                            ]}
                                        />;
                                    }
                                }
                            ]}
                            data={plates}
                            progressPending={isLoading}
                        />
                    </View>
                    <Pagination
                        currentPage={filters.page}
                        totalItems={totalItems}
                        setPage={page => {
                            setFilters(old => ({ ...old, page }));
                        }}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({ ...old, limit: pageSize, page: 0 }));
                        }}
                    />
                </View>
            </View>
            <RegisteredPlateModal
                isModalVisible={isModalVisible || showCreateModal}
                setModalVisible={(val) => {
                    if (showCreateModal) {
                        setShowCreateModal(val);
                    } else {
                        setIsModalVisible(val);
                    }
                }}
                onConfirm={refetch}
                onClose={() => {
                    setSelectedPlate(undefined);
                }}
                selectedPlateId={selectedPlate?.id}
            />
            {selectedPlate && <DeleteRegisteredPlateModal
                isModalVisible={isDeleteModalVisible}
                setModalVisible={setIsDeleteIsModalVisible}
                onConfirm={refetch}
                onClose={() => {
                    setSelectedPlate(undefined);
                }}
                selectedPlate={selectedPlate}
            />}
        </>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    mainHeaderText: {
        fontSize: 16,
        color: theme.colors.neutral['500'],
        fontWeight: '600',
    },
    inputWrapper: {
        flexGrow: 1,
        maxWidth: 290,
        minWidth: 290,
    },
    buttonsContainer: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 10,
    },
    cardListWrapper: {
        flex: 1,
        backgroundColor: theme.colors.dataTable.background,
        padding: 10,
        gap: 10,
        borderRadius: 4,
    },
    cardList: {
        borderWidth: 1,
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        borderColor: theme.colors.dataTable.border,
        backgroundColor: theme.colors.dataTable.background,
        flex: 1,
    },
    flex: {
        flex: 1,
    },
}));
